/**
 * Price import flow (a modal): paste or upload a CSV, parse it client-side,
 * call the import endpoint in preview mode to get the diff, show the grouped
 * new / changed / removed / duplicate lists, then Apply.
 *
 * Expected CSV columns (header row required):
 *   item_code, category, description, first_cost, fob_wholesale,
 *   delivered_wholesale, fob_distributor, delivered_distributor
 */

import { useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { FileUpload, type UploadFile } from '@ampersandbrands/ui';
import {
  useImportConnectPrices,
  isNotAvailable,
  connectMoney,
  type PriceImportRow,
  type PriceImportResult,
} from '@/lib/useConnect';
import { Modal, inputCls, labelCls, primaryBtnCls, secondaryBtnCls } from './connectShared';

const EXPECTED_COLUMNS = [
  'item_code',
  'category',
  'description',
  'first_cost',
  'fob_wholesale',
  'delivered_wholesale',
  'fob_distributor',
  'delivered_distributor',
];

// Minimal CSV parser: handles quoted fields with embedded commas and quotes.
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    if (row.some((c) => c.trim() !== '')) rows.push(row);
  }
  return rows;
}

function rowsToPriceRows(csv: string[][]): { rows: PriceImportRow[]; error: string | null } {
  const headerRow = csv[0];
  if (!headerRow) return { rows: [], error: 'The file is empty.' };
  const header = headerRow.map((h) => h.trim().toLowerCase());
  if (!header.includes('item_code')) {
    return {
      rows: [],
      error: `Missing the required item_code column. Expected columns: ${EXPECTED_COLUMNS.join(', ')}.`,
    };
  }
  const numericCols = new Set([
    'first_cost', 'fob_wholesale', 'delivered_wholesale', 'fob_distributor', 'delivered_distributor',
  ]);
  const out: PriceImportRow[] = [];
  for (let r = 1; r < csv.length; r++) {
    const cells = csv[r] ?? [];
    const rec: Record<string, string | number | null | undefined> = {};
    header.forEach((col, idx) => {
      if (!EXPECTED_COLUMNS.includes(col)) return;
      const raw = (cells[idx] ?? '').trim();
      if (numericCols.has(col)) {
        rec[col] = raw === '' ? null : Number(raw.replace(/[$,]/g, ''));
      } else {
        rec[col] = raw === '' ? undefined : raw;
      }
    });
    if (rec.item_code) out.push(rec as PriceImportRow);
  }
  return { rows: out, error: out.length === 0 ? 'No data rows found under the header.' : null };
}

export function PriceImportFlow({ onClose }: { onClose: () => void }) {
  const importMut = useImportConnectPrices();
  const [raw, setRaw] = useState('');
  // The chosen CSV, kept so the picker can show which file is loaded.
  const [picked, setPicked] = useState<UploadFile[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<PriceImportRow[] | null>(null);
  const [preview, setPreview] = useState<PriceImportResult | null>(null);
  const [applied, setApplied] = useState(false);

  const onFile = async (file: File) => {
    const text = await file.text();
    setRaw(text);
  };

  const runPreview = () => {
    setParseError(null);
    setApplied(false);
    const { rows, error } = rowsToPriceRows(parseCsv(raw));
    if (error) {
      setParseError(error);
      setParsedRows(null);
      setPreview(null);
      return;
    }
    setParsedRows(rows);
    importMut.mutate(
      { mode: 'preview', rows },
      {
        onSuccess: (res) => setPreview(res),
        onError: (e) =>
          setParseError(isNotAvailable(e) ? 'Price import is not available yet.' : e.message),
      },
    );
  };

  const apply = () => {
    if (!parsedRows) return;
    importMut.mutate(
      { mode: 'apply', rows: parsedRows },
      {
        onSuccess: () => setApplied(true),
        onError: (e) =>
          setParseError(isNotAvailable(e) ? 'Price import is not available yet.' : e.message),
      },
    );
  };

  return (
    <Modal
      title="Import prices from file"
      onClose={onClose}
      wide
      footer={
        applied ? (
          <button type="button" onClick={onClose} className={primaryBtnCls}>Done</button>
        ) : preview ? (
          <>
            <button type="button" onClick={() => { setPreview(null); setParsedRows(null); }} className={secondaryBtnCls}>Back</button>
            <button type="button" onClick={apply} disabled={importMut.isPending} className={primaryBtnCls}>
              {importMut.isPending && <Loader2 className="h-3 w-3 animate-spin" />} Apply changes
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onClose} className={secondaryBtnCls}>Cancel</button>
            <button type="button" onClick={runPreview} disabled={importMut.isPending || !raw.trim()} className={primaryBtnCls}>
              {importMut.isPending && <Loader2 className="h-3 w-3 animate-spin" />} Preview
            </button>
          </>
        )
      }
    >
      {applied ? (
        <div className="inline-flex items-center gap-2 text-green-700 dark:text-green-300">
          <Check className="h-4 w-4" /> Prices updated. {preview ? summaryLine(preview) : ''}
        </div>
      ) : preview ? (
        <PreviewDiff result={preview} />
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Expected columns: {EXPECTED_COLUMNS.join(', ')}.
          </p>
          <div>
            <label className={labelCls}>Upload a CSV</label>
            {/* multiple={false} on purpose: the importer parses exactly one
                file. The shared picker still adds drag-and-drop and a clear
                message when something other than a CSV is dropped. */}
            <FileUpload
              files={picked}
              onChange={(next: UploadFile[]) => {
                setPicked(next);
                const f = next[0]?.file;
                if (f) void onFile(f);
              }}
              multiple={false}
              accept={['.csv', 'text/csv']}
              label="Choose a CSV"
            />
          </div>
          <div>
            <label className={labelCls}>Or paste CSV</label>
            {/* STAYS A PLAIN MONOSPACE BOX. Its whole job is to receive raw
                pasted CSV, which is parsed as text. A rich-text editor here
                would inject markup and corrupt the parse. Do not "finish the
                job" by converting this one. */}
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              rows={8}
              placeholder="item_code,category,description,first_cost,fob_wholesale,delivered_wholesale,fob_distributor,delivered_distributor"
              className={`${inputCls} font-mono text-xs`}
            />
          </div>
          {parseError && <p className="text-xs text-red-700 dark:text-red-300">{parseError}</p>}
        </div>
      )}
    </Modal>
  );
}

function summaryLine(r: PriceImportResult): string {
  return `${r.new.length} new, ${r.changed.length} changed, ${r.removed.length} removed.`;
}

function PreviewDiff({ result }: { result: PriceImportResult }) {
  return (
    <div className="space-y-4 text-sm">
      <p className="text-amp-ink dark:text-amp-dark-ink">{summaryLine(result)} {result.duplicates.length > 0 && `${result.duplicates.length} duplicate rows in the file.`}</p>

      <DiffGroup title="New items" count={result.new.length}>
        {result.new.map((r) => (
          <li key={r.item_code} className="font-mono text-xs text-amp-ink dark:text-amp-dark-ink">{r.item_code}</li>
        ))}
      </DiffGroup>

      <DiffGroup title="Changed" count={result.changed.length}>
        {result.changed.map((c, i) => (
          <li key={i} className="text-xs text-amp-ink dark:text-amp-dark-ink">
            <span className="font-mono">{c.item_code}</span>
            <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted"> · {c.field}: </span>
            <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted line-through">{connectMoney(c.old)}</span>
            <span className="text-amp-ink dark:text-amp-dark-ink"> → {connectMoney(c.new)}</span>
          </li>
        ))}
      </DiffGroup>

      <DiffGroup title="Removed" count={result.removed.length}>
        {result.removed.map((r) => (
          <li key={r.item_code} className="font-mono text-xs text-amp-ink dark:text-amp-dark-ink">{r.item_code}</li>
        ))}
      </DiffGroup>

      <DiffGroup title="Duplicates in file" count={result.duplicates.length}>
        {result.duplicates.map((r, i) => (
          <li key={`${r.item_code}-${i}`} className="font-mono text-xs text-amp-ink dark:text-amp-dark-ink">{r.item_code}</li>
        ))}
      </DiffGroup>
    </div>
  );
}

function DiffGroup({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">{title} ({count})</h3>
      {count === 0 ? (
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">None.</p>
      ) : (
        <ul className="max-h-40 overflow-auto space-y-0.5">{children}</ul>
      )}
    </div>
  );
}
